/**
 * @file Get front-end inputs and fetch the layer to display on the map.
 * Handles click on map interaction and automatic trigger for a given app state.
 * Defines the position of the inputs and outputs for different screen sizes.
 */
import { Suspense, lazy, useState, useEffect, useContext } from 'react';

import { useSnapshot } from 'valtio';
import { useEffectOnce } from 'usehooks-ts';

import CssBaseline from '@mui/material/CssBaseline';
import { Unstable_Grid2 as Grid } from '@mui/material';
import MapApplication from './components/MapApplication';
import Loading from './components/Loading.jsx';
import ChildApp from './ChildApp.jsx';
import { EVENT_GROUP_SET_LAYERS } from './constants';
import { registerEvent } from './map/map_events';
import { inputsState, mapState, uiState, urlState } from './state';
import { notifyResizeEvent, notifyChildAppClosed } from './utils.js';
import { ApiContext } from './ApiContext';

const Map = lazy(() => import('./Map.jsx'));
const InputsPanel_common = lazy(() => import('./components/input_panels/InputsPanel_common'));
const InputsPanel_06_hot_days = lazy(() =>
  import('./components/input_panels/InputsPanel_06_hot_days'),
);

const InputsPanel_08_heat_waves_health = lazy(() =>
  import('./components/input_panels/InputsPanel_08_heat_waves_health'),
);
const InputsPanel_09_heat_waves_climatological = lazy(() =>
  import('./components/input_panels/InputsPanel_09_heat_waves_climatological'),
);
const InputsPanel_10_high_utci_days = lazy(() =>
  import('./components/input_panels/InputsPanel_10_high_utci_days'),
);
const InputsPanel_12_total_precipitation = lazy(() =>
  import('./components/input_panels/InputsPanel_12_total_precipitation'),
);
const InputsPanel_16_flood_recurrence_regridded = lazy(() =>
  import('./components/input_panels/InputsPanel_16_flood_recurrence_regridded.jsx'),
);
const InputsPanel_16b_mean_river_discharge_regridded = lazy(() =>
  import('./components/input_panels/InputsPanel_16b_mean_river_discharge_regridded.jsx'),
);
const InputsPanel_17_aridity_actual = lazy(() =>
  import('./components/input_panels/InputsPanel_17_aridity_actual.jsx'),
);
const InputsPanel_19_duration_of_meteorological_droughts = lazy(() =>
  import('./components/input_panels/InputsPanel_19_duration_of_meteorological_droughts.jsx'),
);
const InputsPanel_20_magnitude_of_meteorological_droughts = lazy(() =>
  import('./components/input_panels/InputsPanel_20_magnitude_of_meteorological_droughts.jsx'),
);
const InputsPanel_21b_mean_soil_moisture = lazy(() =>
  import('./components/input_panels/InputsPanel_21b_mean_soil_moisture.jsx'),
);
const InputsPanel_25_snowfall_amount = lazy(() =>
  import('./components/input_panels/InputsPanel_25_snowfall_amount'),
);
const InputsPanel_26_period_with_snow_water_equivalent_above_a_threshold = lazy(() =>
  import(
    './components/input_panels/InputsPanel_26_period_with_snow_water_equivalent_above_a_threshold'
  ),
);
const InputsPanel_27_relative_sea_level_rise_gridded = lazy(() =>
  import('./components/input_panels/InputsPanel_27_relative_sea_level_rise_gridded.jsx'),
);
const InputsPanel_28_extreme_sea_level_gridded = lazy(() =>
  import('./components/input_panels/InputsPanel_28_extreme_sea_level_gridded'),
);
const InputsPanel_29_sst = lazy(() => import('./components/input_panels/InputsPanel_29_sst'));
const InputsPanel_30_marine_heatwaves = lazy(() =>
  import('./components/input_panels/InputsPanel_30_marine_heatwaves'),
);
const InputsPanel_31_dissolved_oxygen_level = lazy(() =>
  import('./components/input_panels/InputsPanel_31_dissolved_oxygen_level'),
);
const InputsPanel_32_ocean_ph_level = lazy(() =>
  import('./components/input_panels/InputsPanel_32_ocean_ph_level'),
);
const InputsPanel_l3_tiger_mosquito_season_length = lazy(() =>
  import('./components/input_panels/InputsPanel_l3_tiger_mosquito_season_length'),
);
const InputsPanel_l4_tiger_mosquito_suitability_index = lazy(() =>
  import('./components/input_panels/InputsPanel_l4_tiger_mosquito_suitability_index'),
);

function App() {
  const [childApp, setChildApp] = useState(false);
  const { apiBase, indicator } = useContext(ApiContext);
  const [openChildapp, setOpenChildapp] = useState(false);
  const snap = useSnapshot(inputsState);
  const {
    horizon,
    month,
    rcp,
    regionalAggregation,
    temporalAggregation,
    mapType,
    threshold,
    altitude,
    level,
    floodRecurrence,
    hydroModel,
  } = snap;

  const handleMapClick = ({ lat, lon, region, regionName, selectedLayer }) => {
    console.debug(`Map clicked at ${lat}, ${lon}, ${region}, ${regionName}, ${selectedLayer}`);
    mapState.lat = lat;
    mapState.lon = lon;
    inputsState.region = region;
    inputsState.regionName = regionName;
    mapState.selectedLayer = selectedLayer;
    // Expands any drawsers that contains outputs
    uiState.drawsers.right = true;
    uiState.drawsers.bottom = true;
    setChildApp(true);
    notifyResizeEvent();
  };

  const handleClose = () => {
    setChildApp(false);
    notifyResizeEvent({ onEnd: notifyChildAppClosed });
  };

  const getInputsPanelComponent = (components) => {
    return components['InputsPanel_' + indicator]
      ? components['InputsPanel_' + indicator]
      : components['InputsPanel_common'];
  };

  // FIXME: To be moved in the Map react component?
  useEffect(() => {
    if (rcp) {
      const qsData = {
        regionalAggregation,
        mapType,
        rcp,
        horizon,
        ...(threshold && { threshold }),
        ...(!isNaN(altitude) && { altitude }),
        ...(level && { level }),
        ...(floodRecurrence && { floodRecurrence }),
        ...(hydroModel && { hydroModel }),
        temporalAggregation: `${temporalAggregation}`,
        ...(temporalAggregation === 'monthly' && { month }),
        ...(temporalAggregation === 'seasonal' && { month }),
      };

      const searchParams = new URLSearchParams(qsData);

      try {
        fetch(`${apiBase}/regions/${indicator}?${searchParams.toString()}`)
          .then((response) => response.json())
          .then((data) => {
            const event = new CustomEvent(EVENT_GROUP_SET_LAYERS, {
              detail: data,
            });
            registerEvent(event);
          });
      } catch (e) {
        console.error('Error fetching data');
        console.trace(e);
      }
    }
  }, [
    altitude,
    apiBase,
    horizon,
    indicator,
    level,
    mapType,
    month,
    rcp,
    regionalAggregation,
    temporalAggregation,
    threshold,
    floodRecurrence,
    hydroModel,
  ]);

  /*
   * Automatically trigger map click if we have a region defined when app loads
   */
  useEffectOnce(() => {
    if (inputsState.region) {
      handleMapClick({
        lat: mapState.lat,
        lon: mapState.lon,
        region: inputsState.region,
        regionName: inputsState.regionName,
        selectedLayer: mapState.selectedLayer,
      });
    }
    if (urlState.region || urlState.lat || urlState.lon) {
      let regionName = '';
      if (urlState.region) {
        regionName = import('./components/json/regions.json')
          .then((data) => {
            return data[urlState.region];
          })
          .catch((error) => {
            console.error('Failed to load data', error);
          });
      }
      handleMapClick({
        lat: urlState.lat,
        lon: urlState.lon,
        region: urlState.region,
        regionName: regionName,
        selectedLayer: urlState.lat || urlState.lon ? '' : urlState.selectedLayer,
      });
      setOpenChildapp(true);
    }
  });

  const components = {
    InputsPanel_06_hot_days,
    InputsPanel_common,
    InputsPanel_08_heat_waves_health,
    InputsPanel_09_heat_waves_climatological,
    InputsPanel_10_high_utci_days,
    InputsPanel_12_total_precipitation,
    InputsPanel_16_flood_recurrence_regridded,
    InputsPanel_16b_mean_river_discharge_regridded,
    InputsPanel_17_aridity_actual,
    InputsPanel_19_duration_of_meteorological_droughts,
    InputsPanel_20_magnitude_of_meteorological_droughts,
    InputsPanel_21b_mean_soil_moisture,
    InputsPanel_25_snowfall_amount,
    InputsPanel_26_period_with_snow_water_equivalent_above_a_threshold,
    InputsPanel_27_relative_sea_level_rise_gridded,
    InputsPanel_28_extreme_sea_level_gridded,
    InputsPanel_29_sst,
    InputsPanel_30_marine_heatwaves,
    InputsPanel_31_dissolved_oxygen_level,
    InputsPanel_32_ocean_ph_level,
    InputsPanel_l3_tiger_mosquito_season_length,
    InputsPanel_l4_tiger_mosquito_suitability_index,
  };

  const SomeComponent = getInputsPanelComponent(components);
  const inputs = <SomeComponent inputsMd="left" inputsXs="top" />;

  const outputs = childApp ? <ChildApp onClose={handleClose} /> : null;

  const openChildappDrawer = openChildapp ? { childApp: true } : null;

  return (
    <>
      <CssBaseline />
      <Grid>
        <Suspense fallback={<Loading />}>
          <MapApplication
            inputs={inputs}
            outputs={outputs}
            inputsMd="left"
            inputsXs="top"
            outputsMd="right"
            outputsXs="bottom"
            drawerDefaultLeft={true}
            drawerDefaultTop={true}
            {...openChildappDrawer}
          >
            <Map onClick={handleMapClick} />
          </MapApplication>
        </Suspense>
      </Grid>
    </>
  );
}

export default App;
